import React, { useCallback, useEffect, useState } from "react";
import {
  Typography,
  TextField,
  Button,
  Container,
  Box,
  Accordion,
  AccordionDetails,
  AccordionSummary,
  styled,
  FormControl,
  FormHelperText,
  Grid,
  MenuItem,
  CircularProgress
} from "@material-ui/core";
import { useHistory, useLocation, useParams } from "react-router-dom";
import { DeliveryLocationModel, IDeliveryLocationModel } from "../../models/DeliveryLocationModel";
import { OrderModel } from "../../models/OrderModel";
import ExpandMoreIcon from '@material-ui/icons/ExpandMore';
import { DeliverySettingsSection } from "./components/DeliverySettingsSection";
import moment, { tz } from "moment-timezone";
import api from "../../utils/api";
import { uniqWith } from "lodash";
import { useUserContext } from "../../contexts/UserContext";
const TZ = "America/Los_Angeles"

const StyledAccordion = styled(Accordion)(({ theme }) => ({
  marginBottom: theme.spacing(2),
}));

const StyledAccordionSummary = styled(AccordionSummary)(({ theme }) => ({
  backgroundColor: theme.palette.grey[50],
  '&.Mui-expanded': {
    minHeight: 'auto',
  },
  '& .MuiAccordionSummary-content': {
    margin: '0px',
    '&.Mui-expanded': {
      margin: '0px',
    },
  },
}));

const StyledAccordionDetails = styled(AccordionDetails)(({ theme }) => ({
  padding: theme.spacing(2),
  flexDirection: 'column',
}));

const StyledSubmitButton = styled(Button)(({ theme }) => ({
  marginLeft: theme.spacing(1),
}));

const StyledCancelButton = styled(Button)(({ theme }) => ({
  marginRight: theme.spacing(1),
}));

const StyledCardHeader = styled("div")(({ theme }) => ({
  padding: theme.spacing(2, 2, 0, 2),
}));

const StyledCardContent = styled(Box)(({ theme }) => ({
  padding: theme.spacing(2),
}));

export const ModifyDeliveryView: React.FC = () => {
  const { jobId } = useParams<{ jobId: string }>(); // Get the jobId from the URL
  const location = useLocation();
  const { isAdmin } = useUserContext();
  const [deliveryLocation, setDeliveryLocation] = useState<IDeliveryLocationModel | null>(null);
  const [loading, setLoading] = useState(true);
  const [updateStatus, setUpdateStatus] = useState<"idle" | "success" | "error">("idle");
  const [updateErrorMessage, setUpdateErrorMessage] = useState("");
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [availableDeliveries, setAvailableDeliveries] = useState<{ startTime: string; driverId: string }[]>([]);
  const [isLoadingAvailableTimes, setIsLoadingAvailableTimes] = useState(false);
  const [hasAvailableTimesError, setHasAvailableTimesError] = useState(false);
  const [deliveryTimeFormValue, setDeliveryTimeFormValue] = useState({
    timeOfDay: "any" as "am" | "pm" | "any",
    deliveryDay: "" as string | null
  });
  const [isSaving, setIsSaving] = useState(false);

  // Form state - matching the IDeliveryLocationModel interface
  const [name, setName] = useState("");
  const [address, setAddress] = useState("");
  const [address2, setAddress2] = useState("");
  const [city, setCity] = useState("");
  const [zipCode, setZipCode] = useState("");
  const [phoneNumber, setPhoneNumber] = useState("");
  const [isContactPersonRequired, setIsContactPersonRequired] = useState<boolean | undefined>(undefined);
  const [isGpsAccurate, setIsGpsAccurate] = useState<boolean | undefined>(undefined);
  const [gpsInaccurateDescription, setGpsInaccurateDescription] = useState("");
  const [isGatedProperty, setIsGatedProperty] = useState<boolean | undefined>(undefined);
  const [gatedPropertyDetails, setGatedPropertyDetails] = useState("");
  const [tankConnectionDetails, setTankConnectionDetails] = useState("");
  const [tankDistance, setTankDistance] = useState("");
  // Keep estimatedMinutes since it's required by the model
  const [estimatedMinutes, setEstimatedMinutes] = useState<number>(90); // Default to 90 minutes

  // Accordion states
  const [isDeliveryDetailsOpen, setIsDeliveryDetailsOpen] = useState(true);
  const [isContactDetailsOpen, setIsContactDetailsOpen] = useState(true);
  const [isAdditionalDetailsOpen, setIsAdditionalDetailsOpen] = useState(true);
  const [isDeliveryTimeOpen, setIsDeliveryTimeOpen] = useState(true);

  // Service from AdminSettings -> services
  const [services, setServices] = useState<{ name: string; default: boolean }[]>([]);
  const [service, setService] = useState<string>("");

  const history = useHistory();
  const urlParams = new URLSearchParams(location.search);
  const shouldNotifyCustomer = isAdmin && urlParams.get("notifyCustomer") === "1";
  const isDeclinedRecreation =
    isAdmin && urlParams.get("recreateDeclined") === "1";
  const destinationPath = isAdmin ? "/admin/orders" : "/orders";

  useEffect(() => {
    const fetchDropdownData = async () => {
      try {
        const services = await api.get("/services");
        setServices(services.data || []);
      } catch (error) {
        console.error("Error fetching dropdown data:", error);
      } finally {
        setLoading(false);
      }
    };
    fetchDropdownData();
  }, [])

  useEffect(() => {
    (async () => {
      try {
        const data = await DeliveryLocationModel.getByOrder(jobId);
        if (data) {
          setDeliveryLocation(data);

          // Populate form fields
          setName(data.name || "");
          setAddress(data.address || "");
          setAddress2(data.address2 || "");
          setCity(data.city || "");
          setZipCode(data.zipCode || "");
          setPhoneNumber(data.phoneNumber || "");
          setIsContactPersonRequired(data.isContactPersonRequired);
          setIsGpsAccurate(data.isGpsAccurate);
          setGpsInaccurateDescription(data.gpsInaccurateDescription || "");
          setIsGatedProperty(data.isGatedProperty);
          setGatedPropertyDetails(data.gatedPropertyDetails || "");
          setTankConnectionDetails(data.tankConnectionDetails || "");
          setTankDistance(data.tankDistance || "");
          setEstimatedMinutes(data.estimatedMinutes || 90);

          // DeliveryLocationModel doesn't have service
          // but the controller does return it
          // through adding it to the deliveryLocation
          setService(data.service || "");
        }
      } catch (error: any) {
        // Check if this is a redirect-like situation (order already confirmed/declined)
        if (error.response?.status === 400 && error.response?.data?.includes("already")) {
          // Extract order ID and status from the error message
          const match = error.response.data.match(/Order (\d+) is already ([\w]+)/);
          if (match) {
            const orderId = match[1];
            const status = match[2];
            history.push(`/confirm-job-order/${orderId}?response=${status}`);
            return;
          }
        }

        const responseData = error?.response?.data;
        setUpdateErrorMessage(
          typeof responseData === "string"
            ? responseData
            : typeof responseData?.message === "string"
            ? responseData.message
            : "Failed to load this delivery. Please return to Orders and try again."
        );
        // Direct links can become stale when status, billing, or ownership changes.
        setUpdateStatus("error");
      } finally {
        setLoading(false);
      }
    })();
  }, [jobId, history]);

  const loadAvailableTimes = useCallback(async () => {
    setIsLoadingAvailableTimes(true);
    setHasAvailableTimesError(false);
    try {
      // The order context lets the server use the target location duration and
      // exclude this order's own QB Time event for both customer and admin edits.
      const times = await OrderModel.getAvailableTimes({ orderId: jobId });
      setAvailableDeliveries(times);
    } catch (error) {
      console.error("Failed to fetch available delivery times:", error);
      setHasAvailableTimesError(true);
    } finally {
      setIsLoadingAvailableTimes(false);
    }
  }, [jobId]);

  // Load available delivery times
  useEffect(() => {
    loadAvailableTimes();
  }, [loadAvailableTimes]);

  const validateForm = () => {
    const newErrors: Record<string, string> = {};

    if (!name.trim()) newErrors.name = "Name is required";
    if (!address.trim()) newErrors.address = "Address is required";
    if (!city.trim()) newErrors.city = "City is required";
    if (!zipCode.trim()) newErrors.zipCode = "ZIP code is required";
    if (!/^\d{10}$/.test(phoneNumber || '')) {
      newErrors.phoneNumber = "Phone number must be 10 digits";
    }
    if (!service.trim()) newErrors.service = "Service is required";

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    try {
      setIsSaving(true);
      setUpdateErrorMessage("");

      // Extract start and end times when provided
      let startTime: string | null = null;
      let endTime: string | null = null;
      const selectedDelivery = deliveryTimeFormValue.deliveryDay
        ? availableDeliveries.find(
            (delivery) => delivery.startTime === deliveryTimeFormValue.deliveryDay
          )
        : undefined;

      if (isDeclinedRecreation && !selectedDelivery) {
        setUpdateStatus("error");
        setUpdateErrorMessage(
          "Select a new available delivery time before recreating this order."
        );
        return;
      }

      if (deliveryTimeFormValue.deliveryDay) {
        // Get exact delivery time selected
        if (selectedDelivery) {
          // Make sure we're using the correct timezone format as expected by server
          const startDate = moment(selectedDelivery.startTime).tz("America/Los_Angeles");
          startTime = startDate.toISOString(); // This ensures proper ISO format with timezone
          endTime = startDate.add(estimatedMinutes, 'minutes').toISOString(); // End time in same format
        }
      }

      const updatedData: IDeliveryLocationModel & {
        driverId?: string;
        recreateDeclined?: boolean;
      } = {
        _id: deliveryLocation?._id || "",
        name,
        address,
        address2,
        city,
        zipCode,
        phoneNumber,
        isContactPersonRequired: isContactPersonRequired ?? false,
        isGpsAccurate: isGpsAccurate ?? false,
        gpsInaccurateDescription,
        isGatedProperty: isGatedProperty ?? false,
        gatedPropertyDetails,
        tankConnectionDetails,
        tankDistance,
        service,
        estimatedMinutes,
        start: startTime ?? "",
        end: endTime ?? "",
        driverId: selectedDelivery?.driverId,
        recreateDeclined: isDeclinedRecreation,
      };

      await DeliveryLocationModel.put(jobId, updatedData, {
        notifyCustomer: shouldNotifyCustomer,
      });
      setUpdateStatus("success");

      // Redirect to orders page after success
      setTimeout(() => {
        history.push(destinationPath);
      }, 2000);
    } catch (error: any) {
      console.error("Failed to update delivery location:", error);
      const availabilityCodes = [
        "DELIVERY_SLOT_UNAVAILABLE",
        "DECLINED_DELIVERY_SLOT_UNAVAILABLE",
        "ASSIGNED_DRIVER_DELIVERY_SLOT_UNAVAILABLE",
      ];
      if (
        error?.response?.status === 409 &&
        availabilityCodes.includes(error?.response?.data?.code)
      ) {
        // The selected option was based on an older availability read. Clear it
        // and refresh the same date/AM/PM controls from QB Time.
        setDeliveryTimeFormValue((current) => ({
          ...current,
          deliveryDay: "",
        }));
        setIsDeliveryTimeOpen(true);
        await loadAvailableTimes();
      }
      setUpdateErrorMessage(
        typeof error?.response?.data === "string"
          ? error.response.data
          : typeof error?.response?.data?.message === "string"
          ? error.response.data.message
          : "Failed to update delivery information. Please try again."
      );
      setUpdateStatus("error");
    } finally {
      setIsSaving(false);
    }
  };

  const handleCancel = () => {
    history.push(destinationPath);
  };

  const handleDeliveryDetailsToggle = () => {
    setIsDeliveryDetailsOpen(!isDeliveryDetailsOpen);
  };

  const handleContactDetailsToggle = () => {
    setIsContactDetailsOpen(!isContactDetailsOpen);
  };

  const handleAdditionalDetailsToggle = () => {
    setIsAdditionalDetailsOpen(!isAdditionalDetailsOpen);
  };

  const handleDeliveryTimeToggle = () => {
    setIsDeliveryTimeOpen(!isDeliveryTimeOpen);
  };

  // Filter available deliveries based on time of day selection
  const filteredDeliveryDays = availableDeliveries.filter((delivery) => {
    if (deliveryTimeFormValue.timeOfDay === "any") {
      return true;
    }
    if (deliveryTimeFormValue.timeOfDay === "am") {
      return moment(delivery.startTime).tz(TZ).hour() < 12;
    }
    if (deliveryTimeFormValue.timeOfDay === "pm") {
      return moment(delivery.startTime).tz(TZ).hour() >= 12;
    }
    return false;
  });
  const deduplicatedDeliveryDays = uniqWith(
    filteredDeliveryDays,
    (a, b) => {
      return moment(a.startTime).tz(TZ).isSame(moment(b.startTime).tz(TZ), "date");
    }
  );
  // Keep normal modifications compact by offering the earliest available slot
  // for each date. Declined recreation still needs an explicit exact time.
  const deliveryTimeOptions = isDeclinedRecreation
    ? filteredDeliveryDays
    : deduplicatedDeliveryDays;

  if (loading) {
    return (
      <Container style={{ minHeight: "75vh", display: "flex", justifyContent: "center", alignItems: "center" }}>
        <Typography>Loading delivery information...</Typography>
      </Container>
    );
  }

  return (
    <Container style={{ minHeight: "75vh", paddingTop: "2rem", paddingBottom: "2rem", display: "flex", justifyContent: "center", alignItems: "center" }}>
      <Box style={{ width: "100%", maxWidth: 800 }}>
        <StyledCardHeader>
          <Typography variant="h4" gutterBottom>
            Modify Delivery Information
          </Typography>

          <Typography variant="subtitle1" gutterBottom>
            {isDeclinedRecreation
              ? "The original delivery time is no longer available. Select a new time to recreate and confirm this order."
              : "Please update the following information for your delivery"}
          </Typography>
        </StyledCardHeader>

        {updateStatus === "success" && (
          <Box mb={2}>
            <Typography style={{ color: "green" }} variant="body1">
              Delivery information updated successfully! Redirecting to orders...
            </Typography>
          </Box>
        )}

        {updateStatus === "error" && (
          <Box mb={2}>
            <Typography style={{ color: "red" }} variant="body1">
              {updateErrorMessage || "Failed to update delivery information. Please try again."}
            </Typography>
          </Box>
        )}

        {/* Contact Details Section - Manually created since the reusable isn't compatible */}
        <StyledAccordion expanded={isContactDetailsOpen} onChange={handleContactDetailsToggle}>
          <StyledAccordionSummary
            expandIcon={<ExpandMoreIcon />}
            aria-controls="contact-details-content"
            id="contact-details-header"
          >
            <div style={{ padding: "1rem 0" }}>
              <Typography variant="h6">Contact Details</Typography>
              <Typography variant="body2" color="textSecondary">
                {isContactDetailsOpen ? "Click to collapse contact details" : "Click to expand contact details"}
              </Typography>
            </div>
          </StyledAccordionSummary>
          <StyledAccordionDetails>
            <StyledCardContent>
              <TextField
                fullWidth
                label="Contact Name"
                value={name}
                onChange={(e) => setName(e.target.value)}
                margin="normal"
                error={!!errors.name}
                helperText={errors.name}
              />

              <TextField
                fullWidth
                label="Phone Number"
                value={phoneNumber}
                onChange={(e) => setPhoneNumber(e.target.value)}
                margin="normal"
                error={!!errors.phoneNumber}
                helperText={errors.phoneNumber}
              />
            </StyledCardContent>
          </StyledAccordionDetails>
        </StyledAccordion>

        {/* Delivery Address Section */}
        <StyledAccordion expanded={isDeliveryDetailsOpen} onChange={handleDeliveryDetailsToggle}>
          <StyledAccordionSummary
            expandIcon={<ExpandMoreIcon />}
            aria-controls="delivery-address-content"
            id="delivery-address-header"
          >
            <div style={{ padding: "1rem 0" }}>
              <Typography variant="h6">Delivery Address</Typography>
              <Typography variant="body2" color="textSecondary">
                {isDeliveryDetailsOpen ? "Click to collapse delivery address" : "Click to expand delivery address"}
              </Typography>
            </div>
          </StyledAccordionSummary>
          <StyledAccordionDetails>
            <StyledCardContent>
              <TextField
                fullWidth
                label="Street Address"
                value={address}
                onChange={(e) => setAddress(e.target.value)}
                margin="normal"
                error={!!errors.address}
                helperText={errors.address}
              />

              <TextField
                fullWidth
                label="Apartment/Suite (Optional)"
                value={address2}
                onChange={(e) => setAddress2(e.target.value)}
                margin="normal"
              />

              <Box display="flex" flexDirection={{ xs: "column", sm: "row" }}>
                <Box mr={{ sm: 2 }} mb={{ xs: 2, sm: 0 }} width={{ xs: "100%", sm: "50%" }}>
                  <TextField
                    fullWidth
                    label="City"
                    value={city}
                    onChange={(e) => setCity(e.target.value)}
                    margin="normal"
                    error={!!errors.city}
                    helperText={errors.city}
                  />
                </Box>
                <Box width={{ xs: "100%", sm: "50%" }}>
                  <TextField
                    fullWidth
                    label="ZIP Code"
                    value={zipCode}
                    onChange={(e) => setZipCode(e.target.value)}
                    margin="normal"
                    error={!!errors.zipCode}
                    helperText={errors.zipCode}
                  />
                </Box>
              </Box>
            </StyledCardContent>
          </StyledAccordionDetails>
        </StyledAccordion>

        {/* Additional Delivery Settings Section */}
        <StyledAccordion expanded={isAdditionalDetailsOpen} onChange={handleAdditionalDetailsToggle}>
          <StyledAccordionSummary
            expandIcon={<ExpandMoreIcon />}
            aria-controls="delivery-settings-content"
            id="delivery-settings-header"
          >
            <div style={{ padding: "1rem 0" }}>
              <Typography variant="h6">Delivery Settings</Typography>
              <Typography variant="body2" color="textSecondary">
                {isAdditionalDetailsOpen ? "Click to collapse delivery settings" : "Click to expand delivery settings"}
              </Typography>
            </div>
          </StyledAccordionSummary>
          <StyledAccordionDetails>
            <StyledCardContent>
              <DeliverySettingsSection
                isContactPersonRequired={isContactPersonRequired}
                setIsContactPersonRequired={setIsContactPersonRequired}
                isGpsAccurate={isGpsAccurate}
                setIsGpsAccurate={setIsGpsAccurate}
                gpsInaccurateDescription={gpsInaccurateDescription}
                setGpsInaccurateDescription={setGpsInaccurateDescription}
                isGatedProperty={isGatedProperty}
                setIsGatedProperty={setIsGatedProperty}
                gatedPropertyDetails={gatedPropertyDetails}
                setGatedPropertyDetails={setGatedPropertyDetails}
                tankConnectionDetails={tankConnectionDetails}
                setTankConnectionDetails={setTankConnectionDetails}
                tankDistance={tankDistance}
                setTankDistance={setTankDistance}
                services={services}
                setService={setService}
                service={service}
              />
            </StyledCardContent>
          </StyledAccordionDetails>
        </StyledAccordion>

        {/* Delivery Time Section */}
        <StyledAccordion expanded={isDeliveryTimeOpen} onChange={handleDeliveryTimeToggle}>
          <StyledAccordionSummary
            expandIcon={<ExpandMoreIcon />}
            aria-controls="delivery-time-content"
            id="delivery-time-header"
          >
            <div style={{ padding: "1rem 0" }}>
              <Typography variant="h6">Delivery Time</Typography>
              <Typography variant="body2" color="textSecondary">
                {isDeliveryTimeOpen ? "Click to collapse delivery time" : "Click to expand delivery time"}
              </Typography>
            </div>
          </StyledAccordionSummary>
          <StyledAccordionDetails>
            <StyledCardContent>
              {availableDeliveries.length > 0 ? (
                <Box>
                  <Typography variant="h6">Choose Preferred Delivery Time</Typography>

                  {isLoadingAvailableTimes ? (
                    <Box display="flex" justifyContent="center">
                      <CircularProgress />
                    </Box>
                  ) : hasAvailableTimesError ? (
                    <Typography color="error">Failed to load available delivery times</Typography>
                  ) : (
                    <Grid container spacing={3}>
                      <Grid item xs={3}>
                        <FormControl style={{ width: "100%" }}>
                          <TextField
                            id="delivery-time-of-day-select"
                            fullWidth
                            select
                            label="Time of Day"
                            value={deliveryTimeFormValue.timeOfDay}
                            onChange={(e) =>
                              setDeliveryTimeFormValue({
                                timeOfDay: e.target.value as "am" | "pm" | "any",
                                deliveryDay: deliveryTimeFormValue.deliveryDay,
                              })
                            }
                            margin="normal"
                          >
                            <MenuItem value={"any"}>Any</MenuItem>
                            <MenuItem value={"am"}>AM</MenuItem>
                            <MenuItem value={"pm"}>PM</MenuItem>
                          </TextField>
                          <FormHelperText>Time of day</FormHelperText>
                        </FormControl>
                      </Grid>
                      <Grid item xs={9}>
                        <FormControl style={{ width: "100%" }}>
                          <TextField
                            id="delivery-date-time-select"
                            fullWidth
                            select
                            label="Delivery Date & Time"
                            value={deliveryTimeFormValue.deliveryDay || ""}
                            onChange={(e) =>
                              setDeliveryTimeFormValue({
                                ...deliveryTimeFormValue,
                                deliveryDay: e.target.value
                              })
                            }
                            margin="normal"
                            disabled={deliveryTimeOptions.length === 0}
                          >
                            <MenuItem value="">Select a time slot</MenuItem>
                            {deliveryTimeOptions.map(({ startTime }) => {
                              const momentDate = moment(startTime).tz(TZ);
                              const dateString = momentDate.format(
                                isDeclinedRecreation
                                  ? "ddd, MMMM Do, YYYY [at] h:mm A"
                                  : "ddd, MMMM Do, YYYY"
                              );
                              return (
                                <MenuItem value={startTime} key={startTime}>
                                  {dateString}
                                </MenuItem>
                              );
                            })
                            };
                          </TextField>
                          <FormHelperText>Select delivery date and time</FormHelperText>
                        </FormControl>
                      </Grid>
                    </Grid>
                  )}
                </Box>
              ) : (
                <Typography>No delivery times available</Typography>
              )}
            </StyledCardContent>
          </StyledAccordionDetails>
        </StyledAccordion>

        <Box display="flex" justifyContent="space-between" mt={2}>
          <StyledCancelButton
            variant="outlined"
            color="secondary"
            onClick={handleCancel}
          >
            Cancel
          </StyledCancelButton>
          <StyledSubmitButton
            variant="contained"
            color="primary"
            onClick={handleSubmit}
            disabled={isSaving}
          >
            {isSaving ? <CircularProgress size={24} /> : "Save Changes"}
          </StyledSubmitButton>
        </Box>
      </Box>
    </Container>
  );
};
